/**
 * Budget Detail Context
 *
 * Provides the ViewModel to all budget-detail child components,
 * eliminating redundant useBudgetAnalytics calls and prop drilling.
 */

import type { BudgetWithProgress } from '@/shared/types';
import { createContext, useContext, type ReactNode } from 'react';
import { type BudgetDetailViewModel, useBudgetDetailViewModel } from './use-budget-detail-viewmodel';

const BudgetDetailContext = createContext<BudgetDetailViewModel | undefined>(undefined);

interface BudgetDetailProviderProps {
  budget: BudgetWithProgress;
  open: boolean;
  children: ReactNode;
}

export function BudgetDetailProvider({ budget, open, children }: BudgetDetailProviderProps) {
  const viewModel = useBudgetDetailViewModel(budget, open);

  return <BudgetDetailContext.Provider value={viewModel}>{children}</BudgetDetailContext.Provider>;
}

export function useBudgetDetail(): BudgetDetailViewModel {
  const ctx = useContext(BudgetDetailContext);
  if (!ctx) {
    throw new Error('useBudgetDetail must be used within a BudgetDetailProvider');
  }
  return ctx;
}
